'use client'

import { useEffect, useState } from 'react'
import {
  CalendarDays,
  CheckCircle2,
  CloudSun,
  Database,
  Leaf,
  LineChart,
  ListChecks,
  ScanLine,
  ShieldCheck,
  Sparkles,
  Sprout,
  TrendingUp,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { useI18n } from '@/components/i18n-provider'
import ShinyText from '@/components/ui/shiny-text'
import Strands from '@/components/ui/strands'
import {
  AI_STAGE_COMPLETION_OFFSETS_MS,
  getCompletedAIStageCount,
  hasAILoadingExceededMinimum,
  MINIMUM_AI_LOADING_MS,
  readAILoadingStartedAt,
  type AILoadingScope,
} from '@/lib/ai-loading'

const PREVIEW_LOOP_MS = 10_800

// 每个分析场景的四个进度通道图标；文案统一走 aiLoading.<scope>.channels.*
const SCOPE_ICONS: Record<AILoadingScope, LucideIcon[]> = {
  recommendation: [Sprout, CloudSun, Database, LineChart],
  yield: [Sprout, CloudSun, Database, TrendingUp],
  price: [Database, CloudSun, LineChart, TrendingUp],
  disease: [ScanLine, Leaf, Database, ShieldCheck],
  calendar: [Sprout, CloudSun, CalendarDays, ListChecks],
}

const CHANNEL_KEYS = ['c1', 'c2', 'c3', 'c4'] as const

type AIGenerationLoadingProps = {
  scope: AILoadingScope
  /** fullscreen 会占满视口并隐藏站点 header/footer；inline 适合嵌在页面区块里 */
  variant?: 'fullscreen' | 'inline'
  previewLoop?: boolean
}

export function AIGenerationLoading({
  scope,
  variant = 'fullscreen',
  previewLoop = false,
}: AIGenerationLoadingProps) {
  const { t } = useI18n()
  const [completedChannelCount, setCompletedChannelCount] = useState(0)
  const [isLongRunning, setIsLongRunning] = useState(false)

  const isFullscreen = variant === 'fullscreen'
  const channels: Array<{ icon: LucideIcon; label: string }> = CHANNEL_KEYS.map((key, index) => ({
    icon: SCOPE_ICONS[scope][index],
    label: t(`aiLoading.${scope}.channels.${key}`),
  }))

  useEffect(() => {
    let stageTimers: number[] = []
    let longRunningTimer = 0
    let previewTimer = 0

    const scheduleStages = (startedAt: number) => {
      stageTimers.forEach((timer) => window.clearTimeout(timer))
      window.clearTimeout(longRunningTimer)

      const now = Date.now()
      setCompletedChannelCount(getCompletedAIStageCount(startedAt, now))
      setIsLongRunning(hasAILoadingExceededMinimum(startedAt, now))

      stageTimers = AI_STAGE_COMPLETION_OFFSETS_MS.map((offset, index) => {
        const remainingMs = Math.max(0, startedAt + offset - now)
        return window.setTimeout(() => setCompletedChannelCount(index + 1), remainingMs)
      })

      const remainingUntilLongRunning = Math.max(0, startedAt + MINIMUM_AI_LOADING_MS - now + 1)
      longRunningTimer = window.setTimeout(() => setIsLongRunning(true), remainingUntilLongRunning)
    }

    if (previewLoop) {
      scheduleStages(Date.now())
      previewTimer = window.setInterval(() => scheduleStages(Date.now()), PREVIEW_LOOP_MS)
    } else {
      scheduleStages(readAILoadingStartedAt(scope))
    }

    return () => {
      stageTimers.forEach((timer) => window.clearTimeout(timer))
      window.clearTimeout(longRunningTimer)
      window.clearInterval(previewTimer)
    }
  }, [previewLoop, scope])

  return (
    <section
      {...(isFullscreen ? { 'data-ai-loading': '' } : {})}
      className={`relative isolate overflow-hidden bg-white text-slate-950 ${
        isFullscreen ? 'min-h-screen' : 'rounded-xl border border-slate-200'
      }`}
    >
      <style>{`
        body:has([data-ai-loading]) header,
        body:has([data-ai-loading]) footer {
          display: none !important;
        }
        @keyframes recommendation-node-pulse {
          0%, 100% { opacity: .4; box-shadow: 0 0 0 0 rgba(15, 23, 42, 0); }
          50% { opacity: 1; box-shadow: 0 0 0 5px rgba(15, 23, 42, .06); }
        }
        @keyframes recommendation-stage-complete {
          from { opacity: 0; transform: scale(.72) rotate(-18deg); }
          to { opacity: 1; transform: scale(1) rotate(0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .recommendation-motion {
            animation-duration: 1ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 1ms !important;
          }
        }
      `}</style>

      <div
        className={`container flex items-center justify-center ${
          isFullscreen ? 'min-h-screen py-14 sm:py-20' : 'py-10 sm:py-14'
        }`}
      >
        <div className="w-full max-w-4xl text-center">
          <div
            className="relative h-[clamp(180px,26vh,280px)] w-full overflow-hidden"
            style={{
              WebkitMaskImage:
                'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
              maskImage:
                'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
            }}
          >
            <div className="absolute left-0 top-1/2 h-[600px] w-full -translate-y-1/2">
              <Strands
                colors={['#F97316', '#7C3AED', '#06B6D4']}
                count={3}
                speed={0.5}
                amplitude={1}
                waviness={1}
                thickness={0.7}
                glow={2.6}
                taper={3}
                spread={1}
                intensity={0.6}
                saturation={2}
                opacity={1}
                scale={1.5}
                glass={false}
                refraction={1}
                dispersion={1}
                glassSize={1}
                hueShift={0}
              />
            </div>
          </div>

          <div
            className="mt-8 flex min-h-10 items-center justify-center"
            role="status"
            aria-live="polite"
          >
            <Sparkles
              className="mr-2 size-5 shrink-0 text-[#475569] sm:size-6"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            <ShinyText
              text={t(
                isLongRunning ? 'aiLoading.statuses.validating' : 'aiLoading.statuses.composing'
              )}
              speed={1.5}
              delay={0}
              color="#475569"
              shineColor="#ffffff"
              spread={120}
              direction="left"
              yoyo={false}
              pauseOnHover={false}
              disabled={false}
              className="recommendation-motion text-xl font-semibold tracking-tight sm:text-2xl"
            />
          </div>

          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
            {t(`aiLoading.${scope}.desc`)}
          </p>

          <div className="mt-9 grid grid-cols-2 gap-2 text-left sm:grid-cols-4 sm:gap-3">
            {channels.map(({ icon: Icon, label }, index) => {
              const isComplete = index < completedChannelCount
              const isActive = index === completedChannelCount

              return (
                <div
                  key={label}
                  className={`group rounded-md border bg-white p-3.5 transition-all duration-500 ${
                    isComplete
                      ? 'border-emerald-600/25 shadow-[0_1px_2px_rgba(15,23,42,.04)]'
                      : isActive
                        ? 'border-slate-900 shadow-[0_6px_20px_rgba(15,23,42,.08)]'
                        : 'border-slate-200 opacity-55'
                  }`}
                  aria-label={`${label}：${
                    isComplete
                      ? t('aiLoading.completed')
                      : isActive
                        ? t('aiLoading.processing')
                        : t('aiLoading.waiting')
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <Icon
                      className={`size-4 transition-colors duration-500 ${
                        isComplete
                          ? 'text-emerald-600'
                          : isActive
                            ? 'text-slate-900'
                            : 'text-slate-300'
                      }`}
                      strokeWidth={1.7}
                    />
                    <span
                      className={`font-mono text-[9px] tracking-[0.18em] transition-colors duration-500 ${
                        isComplete ? 'text-emerald-700/60' : 'text-slate-400'
                      }`}
                      aria-hidden="true"
                    >
                      0{index + 1}
                    </span>
                  </div>
                  <p
                    className={`mt-3 text-xs font-medium transition-colors duration-500 sm:text-sm ${
                      isComplete || isActive ? 'text-slate-900' : 'text-slate-500'
                    }`}
                  >
                    {label}
                  </p>
                  <div
                    className={`mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wider transition-colors duration-500 ${
                      isComplete
                        ? 'text-emerald-700'
                        : isActive
                          ? 'text-slate-600'
                          : 'text-slate-400'
                    }`}
                  >
                    {isComplete ? (
                      <CheckCircle2
                        className="recommendation-motion size-3"
                        style={{
                          animation:
                            'recommendation-stage-complete 420ms cubic-bezier(.2,0,0,1) both',
                        }}
                      />
                    ) : (
                      <span
                        className={`recommendation-motion size-1 rounded-full ${
                          isActive ? 'bg-slate-900' : 'bg-slate-300'
                        }`}
                        style={
                          isActive
                            ? { animation: 'recommendation-node-pulse 1.4s ease-in-out infinite' }
                            : undefined
                        }
                      />
                    )}
                    {isComplete
                      ? t('aiLoading.completed')
                      : isActive
                        ? t('aiLoading.processing')
                        : t('aiLoading.waiting')}
                  </div>
                </div>
              )
            })}
          </div>

          <p className="mt-6 text-xs text-slate-400">{t('aiLoading.note')}</p>
        </div>
      </div>
    </section>
  )
}
